import type { RouteLocationRaw } from 'vue-router';
import { promiseTimeout } from '@vueuse/core';
import { computed, readonly, ref } from 'vue';
import { useRouter } from '#app/composables/router';
import { useNuxtApp } from '#app/nuxt';
import { TRANSITION_DURATION } from '#layers/base/public';

const FADE_OUT_FALLBACK_MS = TRANSITION_DURATION.SLOW + TRANSITION_DURATION.BASE;
const PAGE_SETTLE_FALLBACK_MS = 3000;

const covering = ref(false);

export function useOnboardingExit() {
	const router = useRouter();
	const nuxtApp = useNuxtApp();
	const leaving = ref(false);
	const content = ref<HTMLElement | null>(null);

	const fadeClass = computed(() => [
		'flex min-h-0 flex-1 flex-col transition-all duration-slow ease-out motion-reduce:transition-none',
		{ 'pointer-events-none opacity-0 blur-sm': leaving.value },
	]);

	async function exit(to: RouteLocationRaw) {
		if (leaving.value) return;
		leaving.value = true;
		covering.value = true;

		await whenFadedOut(content.value);

		const { promise: settled, cancel: cancelSettle } = whenPageSettled(nuxtApp);

		try {
			await router.push(to);
			await settled;
		}
		catch (error) {
			cancelSettle();
			leaving.value = false;
			throw error;
		}
		finally {
			covering.value = false;
		}
	}

	return { content, fadeClass, exit, covering: readonly(covering) };
}

function whenPageSettled(nuxtApp: ReturnType<typeof useNuxtApp>) {
	// The executor runs synchronously, so `unregister` is assigned before use.
	let unregister!: () => void;

	const settled = new Promise<void>((resolve) => {
		unregister = nuxtApp.hook('page:loading:end', resolve);
	});

	const promise = Promise.race([settled, promiseTimeout(PAGE_SETTLE_FALLBACK_MS)]).finally(() => unregister());

	return { promise, cancel: unregister };
}

function whenFadedOut(el: HTMLElement | null) {
	if (!el) return Promise.resolve();

	const ac = new AbortController();
	const transitionEnd = new Promise<void>((resolve) => {
		el.addEventListener('transitionend', () => resolve(), { once: true, signal: ac.signal });
	});

	return Promise.race([transitionEnd, promiseTimeout(FADE_OUT_FALLBACK_MS)]).finally(() => ac.abort());
}
